import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import { DocumentCollection } from '@/modules/document/models/DocumentCollection';
import { Document } from '@/modules/document/models/Document';
import { Note } from '@/modules/note/models/Note';
import { ProtectionMandateGenerateOption } from '@/modules/protectionmandate/constants/ProtectionMandateGenerateOption';
import Lang from '@/modules/core/app/services/Lang';
import NumeralService from '@/modules/core/app/services/NumeralService';
import { ColumnFormat } from '@/modules/core/app/constants/data.constants';
import { User } from '@/modules/user/models/User';
import { ProtectionMandataryVerifier } from '@/modules/protectionmandate/models/ProtectionMandataryVerifier';
import { ProtectionMandateFrequencyOfManagementOfPerson } from '@/modules/protectionmandate/constants/ProtectionMandateFrequencyOfManagementOfPerson';
import { EditorTemplate } from '@/modules/export/_modules/editor/models/EditorTemplate';
import { EditorTemplateDocument } from '@/modules/export/_modules/editor/models/EditorTemplateDocument';
import { Estate } from '@/modules/estate/models/Estate';

export class ProtectionMandate extends BaseEntityModel {
  id?: number;
  estateId?: number;
  generateOption?: string;
  allowsCharitableDonation?: boolean | null;
  charitableDonationAmount?: number | null;
  propertyExtraInformation?: string;
  frequencyOfReassessmentOfIncapacity?: number;
  location?: string;
  documents: DocumentCollection;
  notes: Relationship<Note[] | null>;
  primaryPropertyVerifiers: Relationship<User[] | null>;
  substitutePropertyVerifiers: Relationship<User[] | null>;
  editorTemplate: Relationship<EditorTemplate> | null;
  editorTemplateDocument: Relationship<EditorTemplateDocument> | null;
  estate: Relationship<Estate> | null;

  constructor(model: Partial<ProtectionMandate> = {}) {
    super(model);

    this.id = model.id;
    this.estateId = model.estateId;
    this.generateOption = model.generateOption;
    this.allowsCharitableDonation = model.allowsCharitableDonation ?? null;
    this.charitableDonationAmount = model.charitableDonationAmount;
    this.propertyExtraInformation = model.propertyExtraInformation;
    this.frequencyOfReassessmentOfIncapacity = model.frequencyOfReassessmentOfIncapacity;
    this.location = model.location;

    this.documents = DocumentCollection.mapModel(model.documents);
    this.notes = Note.mapModelsOrNull(model.notes);
    this.primaryPropertyVerifiers = User.mapModelsOrNull(model.primaryPropertyVerifiers);
    this.substitutePropertyVerifiers = User.mapModelsOrNull(model.substitutePropertyVerifiers);
    this.editorTemplate = EditorTemplate.mapModelOrNull(
      model.editorTemplateDocument?.editorTemplate
    );
    this.editorTemplateDocument = EditorTemplateDocument.mapModelOrNull(
      model.editorTemplateDocument
    );
    this.estate = Estate.mapModelOrNull(model.estate);
  }

  getModelName(): ModelType {
    return ModelType.PROTECTION_MANDATE;
  }

  setDefaultCharityDonationAmount(): void {
    this.charitableDonationAmount = 1000;
  }

  unsetCharityDonationAmount(): void {
    this.charitableDonationAmount = null;
  }

  get isNoAlreadyHaveGenerateOption(): boolean {
    return this.generateOption === ProtectionMandateGenerateOption.NO_ALREADY_HAVE.key;
  }

  get isYesGenerateOption(): boolean {
    return this.generateOption === ProtectionMandateGenerateOption.YES.key;
  }

  get isNoGenerateOption(): boolean {
    return this.generateOption === ProtectionMandateGenerateOption.NO.key;
  }

  get hasDocumentWithEstateTask(): boolean {
    return !!(
      this.documents?.hasItems &&
      this.documents?.items?.some(
        (document: Document) => !!document.estateTasks && document.estateTasks.length > 0
      )
    );
  }
  get formattedReassessmentOfIncapacity(): string {
    return !!this.frequencyOfReassessmentOfIncapacity &&
      this.frequencyOfReassessmentOfIncapacity > 1
      ? Lang.t('protectionmandate.every-x-year', {
          number: this.frequencyOfReassessmentOfIncapacity
        })
      : Lang.t('protectionmandate.every-year');
  }
  get protectionMandateText(): string {
    return Lang.t('document.protection-mandate').toLowerCase();
  }
  get formattedCharitableDonationAmount(): string {
    return NumeralService.formatValue(this.charitableDonationAmount, ColumnFormat.FORMAT_CURRENCY);
  }

  get hasCharitableDonationsText(): boolean {
    return this.allowsCharitableDonation
      ? Lang.t('protectionmandate.would-like-to-donate')
      : Lang.t('protectionmandate.does-not-want-to-donate');
  }

  get charitableAmountText(): string {
    return this.allowsCharitableDonation && this.charitableDonationAmount
      ? Lang.t('protectionmandate.maximum-amount-per-year', {
          amount: this.charitableDonationAmount
        })
      : '';
  }

  get formattedProtectionPropertyFrequency(): string {
    const frequency: number | null = this.protectionPropertyFrequencyValue;

    switch (frequency) {
      case ProtectionMandateFrequencyOfManagementOfPerson.YEAR_1.value:
        return ProtectionMandateFrequencyOfManagementOfPerson.YEAR_1.description;
      case ProtectionMandateFrequencyOfManagementOfPerson.YEAR_2.value:
        return ProtectionMandateFrequencyOfManagementOfPerson.YEAR_2.description;
      case ProtectionMandateFrequencyOfManagementOfPerson.YEAR_3.value:
        return ProtectionMandateFrequencyOfManagementOfPerson.YEAR_3.description;
    }

    return '';
  }

  get isDocumentGenerated(): boolean {
    return (
      (!!this.documents?.estateProtectionMandateDocx?.id ||
        !!this.documents?.estateProtectionMandate?.id) &&
      this.isYesGenerateOption
    );
  }

  get protectionPropertyFrequencyValue(): number | null {
    if (this.primaryPropertyVerifiers?.length) {
      return Number(this.primaryPropertyVerifiers[0].pivot?.frequency);
    }

    return null;
  }
  getNewVerifierModel(): User {
    return User.mapModel({ pivot: ProtectionMandataryVerifier.mapModel({ frequency: undefined }) });
  }
}
